type AnalyticsEvent = { event?: string; properties?: Record<string, unknown> };
export type NoiseSource =
	| "wallet_injection"
	| "darkreader"
	| "script_error_no_stack"
	| "object_not_found_matching_id"
	| "resize_observer_loop";

function record(value: unknown): Record<string, unknown> | undefined {
	return value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: undefined;
}

/** Fixed categories only. The original event and exception details are never removed. */
export function exceptionNoiseSource(event: AnalyticsEvent): NoiseSource | undefined {
	if (event.event !== "$exception") return undefined;
	const properties = event.properties ?? {};
	const exceptions =
		Array.isArray(properties.$exception_list) && properties.$exception_list.length
			? properties.$exception_list.map(record)
			: [{ value: properties.$exception_message, stacktrace: { frames: properties.$exception_frames } }];
	const categories = exceptions.map((exception): NoiseSource | undefined => {
		if (!exception) return undefined;
		const message = typeof exception.value === "string" ? exception.value.trim() : "";
		const stacktrace = record(exception.stacktrace);
		const frames = Array.isArray(stacktrace?.frames) ? stacktrace.frames : [];
		const frameText = frames
			.flatMap((frame) => {
				const fields = record(frame);
				return [fields?.filename, fields?.function].filter(
					(value): value is string => typeof value === "string",
				);
			})
			.join(" ");
		if (
			/\bwindow\.ethereum\b|\bethereum\.selectedAddress\b/i.test(message) ||
			(/selectedAddress|ethereum/i.test(message) &&
				/(?:chrome-extension:|moz-extension:|brave:).*?(?:wallet|inpage|ethereum)/i.test(frameText))
		)
			return "wallet_injection";
		if (/\bdarkreader\b|__darkreader__/i.test(`${message} ${frameText}`)) return "darkreader";
		const rawStack =
			typeof properties.$exception_stack_trace_raw === "string" ? properties.$exception_stack_trace_raw : "";
		if (/^Script error\.?$/i.test(message) && frames.length === 0 && !/\n\s*at\b|@https?:\/\//.test(rawStack))
			return "script_error_no_stack";
		if (
			frames.length === 0 &&
			/^(?:Non-Error promise rejection captured with value:\s*)?Object Not Found Matching Id:\s*\d+,\s*MethodName:[A-Za-z_][A-Za-z0-9_]*(?:,\s*ParamCount:\d+)?$/i.test(
				message,
			)
		)
			return "object_not_found_matching_id";
		if (/^ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)\.?$/i.test(message))
			return "resize_observer_loop";
		return undefined;
	});
	// A real error in a cause chain must not be hidden behind a noisy companion.
	return categories[0] && categories.every((category) => category === categories[0])
		? categories[0]
		: undefined;
}

type ScannerState = {
	mailAt: number;
	interacted: boolean;
	footerPaths: string[];
	lastFooterAt: number;
	suspected: boolean;
};
type StorageLike = Pick<Storage, "getItem" | "setItem">;
const STORAGE_KEY = "eurolabs_ph_mail_scanner_v1";
const MAIL_WINDOW_MS = 10 * 60_000;
const SCAN_WINDOW_MS = 10_000;
const emptyState = (): ScannerState => ({
	mailAt: 0,
	interacted: false,
	footerPaths: [],
	lastFooterAt: 0,
	suspected: false,
});

function footerPath(pathname: string): string | undefined {
	const path = pathname.replace(/\/$/, "").replace(/^\/[a-z]{2}(?=\/)/, "");
	return ["/privacy", "/impressum"].includes(path) ? path : undefined;
}

function isMailUrl(value: string | undefined): boolean {
	try {
		const url = new URL(value ?? "");
		const query = url.searchParams;
		return (
			/^(?:email|newsletter)$/i.test(query.get("utm_medium") ?? "") ||
			/^newsletter$/i.test(query.get("utm_source") ?? "") ||
			/(?:^|\.)safelinks\.protection\.outlook\.com$/i.test(url.hostname)
		);
	} catch {
		return false;
	}
}

/** Only booleans, a short fixed path allowlist and timestamps are persisted in this tab. */
export function createPostHogNoiseClassifier(storage?: StorageLike) {
	let state = emptyState();
	try {
		const saved = record(JSON.parse(storage?.getItem(STORAGE_KEY) ?? "null"));
		if (
			saved &&
			typeof saved.mailAt === "number" &&
			Number.isFinite(saved.mailAt) &&
			typeof saved.lastFooterAt === "number" &&
			Number.isFinite(saved.lastFooterAt) &&
			typeof saved.interacted === "boolean" &&
			typeof saved.suspected === "boolean" &&
			Array.isArray(saved.footerPaths) &&
			saved.footerPaths.length <= 2 &&
			saved.footerPaths.every((path) => typeof path === "string" && footerPath(path) === path)
		)
			state = saved as unknown as ScannerState;
	} catch {
		/* Storage and JSON failures never affect capture. */
	}
	let persisted = "";
	const persist = () => {
		try {
			const value = JSON.stringify(state);
			if (value !== persisted) {
				storage?.setItem(STORAGE_KEY, value);
				persisted = value;
			}
		} catch {
			/* Optional evidence only. */
		}
	};
	return {
		recordInteraction(trusted: boolean) {
			if (!trusted) return;
			state.interacted = true;
			state.suspected = false;
			persist();
		},
		labels(
			event: AnalyticsEvent,
			context: { url?: string; referrer?: string; userAgent?: string; now?: number },
		) {
			const noise = exceptionNoiseSource(event);
			const labels: { noise_source?: NoiseSource; suspected_bot?: true; bot_reason?: "mail_link_scanner" } =
				noise ? { noise_source: noise } : {};
			const now = context.now ?? Date.now();
			if (state.mailAt && (now - state.mailAt > MAIL_WINDOW_MS || now < state.mailAt)) state = emptyState();
			if (!state.mailAt && (isMailUrl(context.url) || isMailUrl(context.referrer))) state.mailAt = now;
			let path: string | undefined;
			try {
				path = footerPath(new URL(context.url ?? "").pathname);
			} catch {
				/* Not a navigation event. */
			}
			const windowsChrome =
				/Windows NT/i.test(context.userAgent ?? "") &&
				/(?:Chrome|Chromium)\/\d+/i.test(context.userAgent ?? "") &&
				!/(?:Edg|OPR)\//i.test(context.userAgent ?? "");
			if (state.mailAt && !state.interacted && windowsChrome && path) {
				if (now - state.lastFooterAt > SCAN_WINDOW_MS) state.footerPaths = [];
				if (!state.footerPaths.includes(path)) state.footerPaths.push(path);
				state.lastFooterAt = now;
				const explicitScanner = /Microsoft (?:Defender|Office)|SafeLinks|URLScan/i.test(
					context.userAgent ?? "",
				);
				// A SafeLinks referrer alone can be a real reader. Require a rapid sequence,
				// an explicit scanner agent, or the observed Defender injected exception.
				if (state.footerPaths.length >= 2 || explicitScanner || noise === "object_not_found_matching_id")
					state.suspected = true;
			}
			if (state.suspected && !state.interacted && windowsChrome) {
				labels.suspected_bot = true;
				labels.bot_reason = "mail_link_scanner";
			}
			persist();
			return labels;
		},
	};
}
